import './styles.scss'

import { EventImpl } from '@fullcalendar/react/protected-api'
import { Box, Grid, lighten, useTheme } from '@mui/material'
import { darken } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { useTour } from '@reactour/tour'
import { Notes, RecurrentItemCalendar, TypeNote, TypeTask } from 'assets/icons'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useEventGlobals } from 'components/providers/EventGlobalsProvider'
import Item from 'components/tfmDateSortedItems/Item'
import SomedayItem from 'components/tfmDateSortedItems/SomedayItem'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import dayjs from 'dayjs'
import { usePendingEventAutoOpen } from 'hooks/usePendingEventAutoOpen'
import { t } from 'i18next'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { CSSProperties } from 'styled-components'
import { CalendarRange } from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import { defaultGroupImage } from 'utils/api/groups'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { isEventInCalendar } from 'utils/calendarInheritance'
import { APP_COLOR, isColorLight } from 'utils/colors'
import {
  bookedEvent,
  eventIdForContainer,
  eventLengthInDays,
  provideEventTitle
} from 'utils/event'
import {
  getGroupColor,
  isConvertedGroup,
  isConvertedGroupId
} from 'utils/groups'
import { isTextRTL } from 'utils/lang'
import { defaultTaskColor, provideTaskColor } from 'utils/masterLabel'
import { colorFromMetaData } from 'utils/note_meta_data'
import { isTrulyEmpty } from 'utils/object'
import { findStatusForInstance } from 'utils/recurrence'
import { rtlString as fixStringForRtl } from 'utils/strings'
import { getCachedConvertedCalendarsFromLocalStorage } from 'utils/third_party_calendars'

import Avatar from '../../../components/badges/Avatar'
import ObserveService from '../../../services/db/Observe'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  MasterLabelDoc,
  ParticipantDataDoc,
  ProfileDoc,
  TaskType,
  UserSettingsDoc
} from '../../../types/api/changes'
import { Model } from '../../../types/model'
import { eventStatusIcon } from '../../../utils/api/events'
import { EventPopupContent } from '../../popups/eventInfo/EventPopup'
import { useEventPopoverCallbacks } from '../eventPopover/EventPopoverProvider'
import { LocationLabel } from '../styled'
import dateToTimeString from '.'
import * as Styled from './styled'

export interface EventProps {
  topGroup?: Model<GroupDoc>
  title: string
  event?: Model<EventDoc> | EventDoc | undefined
  group?: Model<GroupDoc> | GroupDoc | undefined
  account?: Model<AccountDoc> | AccountDoc | undefined
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc | undefined
  groups?: Model<GroupDoc>[] | GroupDoc[]
  masterLabel?: Model<MasterLabelDoc>
  instanceStart: number
  instanceEnd: number
  localParticipants?: Model<ParticipantDataDoc>[]
  profile?: Model<ProfileDoc>
  isStart?: boolean // for the long events, we need to know that we showing popup only for the start of the event
  closeAll?: boolean // for the drag/resize callbacks when we want to hide all opened popups
  alwaysFullSize?: boolean // to display event row fullsize always without checks which view is selected
  onEventDetailsClose?: () => void
  ignoreDim?: boolean
  calendarView?: CalendarRange
  doNotScrollToEvent?: boolean
  fc_id?: string
  dynamicDesign?: boolean
  isEnd?: boolean
  embedMode?: boolean
  searchMode?: boolean
  fcEvent?: EventImpl
}

interface DetermineEventColorParams {
  event?: Model<EventDoc> | EventDoc
  group?: Model<GroupDoc> | GroupDoc
  selectedGroup?: Model<GroupDoc> | GroupDoc
  referenceGroup?: Model<GroupDoc> | GroupDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  taskDesign: boolean
  embedMode?: boolean
  currentUserId: string | null
  convertedCalendars: ReturnType<
    typeof getCachedConvertedCalendarsFromLocalStorage
  >
}

function determineEventColor(params: DetermineEventColorParams): string {
  const {
    event,
    group,
    selectedGroup,
    referenceGroup,
    userSettings,
    taskDesign,
    embedMode,
    currentUserId,
    convertedCalendars
  } = params

  const effectiveReferenceGroup = referenceGroup ?? event?.referenceGroup

  // Task design always uses white
  if (!isGroupcalApp() && taskDesign) {
    return 'var(--color-surface-hover)'
  }

  if (currentUserId === null && event?.GroupID !== group?._id) {
    // we have forwareded event in read only, fallback to group color
    return selectedGroup?.GroupColor ?? APP_COLOR
  }

  // Embed mode uses group color or default
  if (embedMode) {
    return group?.GroupColor ?? APP_COLOR
  }

  // Default fallback color
  const defaultColor = !isTrulyEmpty(selectedGroup?.GroupColor)
    ? selectedGroup!.GroupColor
    : undefined

  try {
    const groupsSettings = userSettings?.GroupsSettings

    if (!event) {
      Logger.blue('Non event render', { group, selectedGroup, userSettings })
      const groupSettingsColorSelected =
        groupsSettings?.[selectedGroup?._id ?? '']?.GroupColor

      const pendingEventColorForMember =
        selectedGroup?.Participants?.[currentUserId ?? '']?.MemberColor

      if (!isTrulyEmpty(pendingEventColorForMember)) {
        return pendingEventColorForMember!
      }

      if (!isTrulyEmpty(groupSettingsColorSelected)) {
        return groupSettingsColorSelected!
      }

      if (isTrulyEmpty(defaultColor)) {
        if (is24meApp()) return 'var(--color-surface-hover)'
        else return APP_COLOR
      }

      return defaultColor || APP_COLOR
    }

    // Check for member color in Groupcal app
    const memberColor =
      effectiveReferenceGroup?.Participants?.[event.OwnerID ?? '']?.MemberColor

    if (!isTrulyEmpty(memberColor)) {
      return memberColor!
    }

    const isConvertedEvent =
      isConvertedGroup(group) || isConvertedGroupId(event.GroupID)

    // 0. Metadata color for converted groups. In some paths (notably test
    // runs), `group` can be temporarily unresolved while `event.GroupID`
    // is already available, so also check by GroupID.
    if (isConvertedEvent) {
      const metadataColor = colorFromMetaData(event)
      if (metadataColor) {
        return metadataColor
      }
    }

    // Priority order for color determination:
    // 1. Event color (for 24me app or converted events)
    const hasEventColor =
      !isTrulyEmpty(event.Color) &&
      (is24meApp() || isConvertedGroupId(event.GroupID))

    if (hasEventColor) {
      return event.Color!
    }

    // converted cached group color
    const colorFromCache = convertedCalendars.find(
      (calendar) => calendar.id === event.ThirdPartyID
    )?.backgroundColor

    if (isConvertedGroupId(event.GroupID) && colorFromCache) {
      return darken(colorFromCache, 0.15)
    }

    // 2. Group settings color for this event's group
    const groupSettingsColor = groupsSettings?.[event.GroupID ?? '']?.GroupColor
    if (groupSettingsColor) {
      return groupSettingsColor
    }

    // 3. Reference group color
    const referenceGroupColor = getGroupColor(
      effectiveReferenceGroup,
      groupsSettings
    )
    if (referenceGroupColor) {
      return referenceGroupColor
    }

    // 4. Selected group color
    const selectedGroupColor = getGroupColor(group, groupsSettings)
    if (selectedGroupColor) {
      return selectedGroupColor
    }
  } catch (e) {
    console.error('Error determining event color:', e)
  }

  // Final fallback
  return defaultColor || APP_COLOR
}

function Event(rawProps: EventProps) {
  const globals = useEventGlobals()
  const props: EventProps = !globals
    ? rawProps
    : ({
        ...rawProps,
        account: rawProps.account ?? globals.account,
        userSettings: rawProps.userSettings ?? globals.userSettings,
        groups: rawProps.groups ?? globals.groups,
        profile: rawProps.profile ?? globals.profile,
        localParticipants:
          rawProps.localParticipants ?? globals.localParticipants,
        masterLabel: rawProps.masterLabel ?? globals.masterLabel,
        topGroup: rawProps.topGroup ?? globals.topGroup,
        group:
          rawProps.group ??
          (rawProps.event?.GroupID
            ? globals.groupsById?.get(rawProps.event.GroupID)
            : undefined)
      } as EventProps)
  const theme = useTheme()
  const tfmTourProvider = useTfmTourProvider()
  const calendar = useCalendar()
  const tour = useTour()
  const eventView = calendar.view
  const isMonthView = eventView === CalendarRange.MONTH
  const isListView =
    eventView === CalendarRange.LISTWEEK || eventView === CalendarRange.LISTDAY
  const { openPopover, closePopover } = useEventPopoverCallbacks()

  const taskDesign =
    isTaskDesign(props.event) || (is24meApp() && !calendar.defaultGroup)

  const isPendingEvent =
    props.event?._id?.length === 0 && props.event?.local_id?.length === 0

  const initialOpenState = isPendingEvent

  const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)
  const selectedGroupId = LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
  const convertedCalendars =
    globals?.convertedCalendars ?? getCachedConvertedCalendarsFromLocalStorage()

  // Resolve the reference group from the live selected group instead of trusting
  // the value baked into the event at FC mapping time. The baked value is keyed
  // only by event.GroupID and never re-resolves on default-group switch, so for
  // events whose selected group is in SupplementaryGroupsIDs the color/avatar
  // would otherwise lag behind the user's current group selection.
  // Must reflect the explicit groups-list selection; empty string means
  // "All calendars" and should not be replaced by defaultGroup.
  const liveSelectedGroupId = selectedGroupId
  const effectiveReferenceGroup = useMemo(() => {
    const ev = props.event
    if (!ev) return undefined
    if (liveSelectedGroupId) {
      if (liveSelectedGroupId === ev.GroupID) {
        return globals?.groupsById?.get(ev.GroupID) ?? ev.referenceGroup
      }
      if (isEventInCalendar(ev, liveSelectedGroupId)) {
        return (
          globals?.groupsById?.get(liveSelectedGroupId) ?? ev.referenceGroup
        )
      }
    }
    // Fall back to the live group from globals when the event has no
    // pre-attached referenceGroup. ListView does not bake referenceGroup onto
    // events (CalendarView/GalleryView do), so without this lookup the event
    // color/avatar would render with the default app color.
    return ev.referenceGroup ?? globals?.groupsById?.get(ev.GroupID ?? '')
  }, [
    liveSelectedGroupId,
    props.event?.GroupID,
    props.event?.SupplementaryGroupsIDs,
    props.event?.inheritedGroups,
    props.event?.referenceGroup,
    globals?.groupsById
  ])

  const color = determineEventColor({
    event: props.event,
    group: props.group,
    selectedGroup: calendar.defaultGroup ?? undefined,
    referenceGroup: effectiveReferenceGroup,
    userSettings: props.userSettings,
    taskDesign,
    embedMode: props.embedMode,
    currentUserId,
    convertedCalendars
  })

  function buildEventTime() {
    const formattedStart = eventStart.format('MMM D')
    const formattedEnd = eventEnd.format('MMM D')

    if (isMonthView) {
      if (props.event?.AllDay === '1') {
        return ''
      }
    }

    if (props.event?.AllDay === '1' && formattedStart === formattedEnd) {
      return t('allDay')
    }

    if (formattedStart !== formattedEnd) {
      return eventStart.format('MMM D') + ' - ' + eventEnd.format('MMM D')
    }

    return fixStringForRtl(
      `${
        props.event?.AllDay === '1'
          ? `${dateToTimeString(eventStart.toDate(), true).toLowerCase()}`
          : (() => {
              const startTime = dateToTimeString(
                eventStart.toDate(),
                true
              ).toLowerCase()
              const endTime = dateToTimeString(
                eventEnd.toDate(),
                true
              ).toLowerCase()

              // Extract the period (am/pm) from the start and end times
              const startPeriod = startTime.includes('am') ? 'am' : 'pm'
              const endPeriod = endTime.includes('am') ? 'am' : 'pm'

              // If the periods are the same, omit it from the start time
              if (startPeriod === endPeriod) {
                return `${startTime.replace('am', '').replace('pm', '')} - ${endTime}`
              }

              // Otherwise, include the full times
              return `${startTime} - ${endTime}`
            })()
      }`
    )
  }

  const eventStart = dayjs.unix(props.instanceStart / 1000)
  const eventEnd = dayjs.unix(props.instanceEnd / 1000)
  const eventTimeText = buildEventTime()
  const isMonthCompact = isMonthView && !props.searchMode
  let eventSubText = isMonthCompact ? '' : eventTimeText
  let fontSize = isMonthCompact ? '11.5px' : '12px'
  const dir = isMonthCompact
    ? 'ltr'
    : isTextRTL(props.event?.Text)
      ? 'rtl'
      : 'ltr' // Determine the text direction

  const eventRef = useRef<HTMLDivElement>(null)
  const lastReminderAutoOpenIdRef = useRef<string | null>(null)
  const pendingReleaseCleanupRef = useRef<(() => void) | null>(null)
  const currentEventContainerId =
    eventIdForContainer(props.event, props.instanceStart) ?? ''
  const pendingPopoverShadow =
    theme.palette.mode === 'dark'
      ? '0 20px 46px rgb(105 105 105 / 62%), 0 10px 24px rgb(32 31 31 / 46%), 0 0 0 1px rgba(255, 255, 255, 0.14), 0 0 24px rgba(255, 255, 255, 0.22), 0 0 40px rgba(255, 255, 255, 0.08)'
      : '0 16px 36px rgba(0, 0, 0, 0.4), 0 6px 16px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(0, 0, 0, 0.18)'

  useEffect(() => {
    return () => {
      pendingReleaseCleanupRef.current?.()
      pendingReleaseCleanupRef.current = null
    }
  }, [])

  const clearDraggingFlag = useCallback(() => {
    window.setTimeout(() => {
      LocalStorage.remove(LocalStorageKey.DRAGGING)
    }, 25)
  }, [])

  const handlePointerDown = useCallback(() => {
    LocalStorage.set(LocalStorageKey.DRAGGING, '1')

    pendingReleaseCleanupRef.current?.()

    const handleWindowRelease = () => {
      pendingReleaseCleanupRef.current?.()
      pendingReleaseCleanupRef.current = null
      clearDraggingFlag()
    }

    const cleanup = () => {
      window.removeEventListener('mouseup', handleWindowRelease, true)
      window.removeEventListener('touchend', handleWindowRelease, true)
      window.removeEventListener('touchcancel', handleWindowRelease, true)
      window.removeEventListener('pointerup', handleWindowRelease, true)
      window.removeEventListener('pointercancel', handleWindowRelease, true)
      window.removeEventListener('dragend', handleWindowRelease, true)
    }

    pendingReleaseCleanupRef.current = cleanup

    window.addEventListener('mouseup', handleWindowRelease, true)
    window.addEventListener('touchend', handleWindowRelease, true)
    window.addEventListener('touchcancel', handleWindowRelease, true)
    window.addEventListener('pointerup', handleWindowRelease, true)
    window.addEventListener('pointercancel', handleWindowRelease, true)
    window.addEventListener('dragend', handleWindowRelease, true)
  }, [clearDraggingFlag])

  usePendingEventAutoOpen(
    props.event,
    props.instanceStart,
    props.instanceEnd,
    eventRef
  )

  useEffect(() => {
    const requestedVisibleEvent = LocalStorage.get(
      LocalStorageKey.VISIBLE_EVENT
    )

    if (
      !requestedVisibleEvent ||
      requestedVisibleEvent !== currentEventContainerId
    )
      return

    if (
      LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1' ||
      LocalStorage.get(LocalStorageKey.DRAGGING) === '1'
    ) {
      return
    }

    if (lastReminderAutoOpenIdRef.current === requestedVisibleEvent) {
      return
    }

    if (eventRef.current?.isConnected) {
      lastReminderAutoOpenIdRef.current = requestedVisibleEvent
      eventRef.current.click()
    }
  }, [currentEventContainerId, props.event?._rev, props.instanceStart])

  if (!props.event) {
    return (
      <Styled.EventContent
        sx={{
          position: 'relative',
          zIndex: 12,
          borderRadius: '5px',
          boxShadow: `${pendingPopoverShadow} !important`,
          transition: 'box-shadow 0.3s ease',
          border:
            !isMonthView || taskDesign
              ? `1px solid ${taskDesign ? 'black' : 'white'}`
              : undefined
        }}
        padding={0}
        color={color}
        fontcolor={taskDesign ? 'black' : 'white'}
        fontSize={fontSize}
        style={{ direction: dir }}
        showstripes={undefined}
      >
        <Box
          paddingLeft={isMonthView ? '0.15rem' : '0.3rem'}
          paddingRight={isMonthView ? '0.15rem' : '0.3rem'}
          paddingTop={
            isMonthView ? '0.05rem' : props.fcEvent?.allDay ? 0 : '0.15rem'
          }
          sx={{
            width: '100%',
            height: props.fcEvent?.allDay ? '17px !important' : '100%',
            flexDirection: 'column',
            display: 'flex',
            fontSize: isMonthView ? '11.5px' : '12px',
            lineHeight: '16px',
            fontWeight: 400,
            color: taskDesign ? 'black' : 'white'
          }}
        >
          {taskDesign ? t('newTask') : t('newEvent')}
          <SubText text1={eventSubText} />
        </Box>
      </Styled.EventContent>
    )
  }

  if (
    (props.event?._id?.length ?? 0) > 0 ||
    props.event?.AllDay === '1' ||
    props.event.late
  ) {
    eventSubText = isTrulyEmpty(props.event.Location?.Address)
      ? eventSubText
      : `${eventTimeText}, ${props.event.Location?.Address}`
  }

  let direction =
    isMonthView || props.event.AllDay === '1' || props.event.late
      ? 'row-reverse'
      : 'column'

  const location =
    !isMonthView &&
    props.event.Location &&
    props.event.Location.Address != undefined

  const statusSize = 14
  const duration = Number(props.event.EndDate) - Number(props.event.StartDate)
  const allCalendarMode = !selectedGroupId
  const ownerParticipant = props.localParticipants?.find(
    (p) => p.AccountID === props.event?.OwnerID
  )
  const isCurrentUserOwner = props.event.OwnerID === currentUserId

  const isGuest = !currentUserId

  const activeGroupId = liveSelectedGroupId
  const fullyVisibleEvent =
    props.embedMode ||
    isTrulyEmpty(activeGroupId) ||
    isEventInCalendar(props.event, activeGroupId) ||
    effectiveReferenceGroup?._id === activeGroupId
  const confirmStatus =
    props.event.ParticipantsStatus?.[currentUserId ?? '']
      ?.ParticipantConfirmationStatus
  const pending =
    ((props.event._id?.length ?? 0) > 0 &&
      (props.event?.RequestConfirmation ?? '') === '1' &&
      props.event.TaskType !== TaskType.personalEvent &&
      (!confirmStatus || confirmStatus === '1' || confirmStatus === '4')) ||
    props.event?.attendees?.find(
      (attendee) =>
        attendee.self &&
        attendee.responseStatus?.toLowerCase() !== 'accepted' &&
        attendee.responseStatus?.toLowerCase() !== 'declined'
    ) != null

  const eventDuration = (props.instanceEnd - props.instanceStart) / 60000 //check if this less than 60, that means we have small event rectangle
  let smallEventGap = 59

  if (props.event.Location && typeof props.event.Location !== 'string')
    smallEventGap = 74

  const taskColor = provideTaskColor(
    props.masterLabel,
    props.event,
    props.account
  )

  let eventGroupColor =
    isGroupcalApp() &&
    effectiveReferenceGroup?.Participants?.[props.event?.OwnerID ?? '']
      ?.MemberColor
      ? (props.userSettings?.GroupsSettings?.[
          effectiveReferenceGroup?._id ?? ''
        ]?.GroupColor ?? effectiveReferenceGroup?.GroupColor)
      : undefined

  const currentConvertedCalendar = convertedCalendars.find(
    (cal) => cal.id === props.event?.ThirdPartyID
  )
  let colorDifferentThanCalendar = false

  if (!isTrulyEmpty(currentConvertedCalendar?.backgroundColor)) {
    const parentCalendarColor = currentConvertedCalendar?.backgroundColor!

    if (
      props.event.TaskType === TaskType.personalEvent &&
      darken(parentCalendarColor, 0.15) !== color &&
      parentCalendarColor !== color
    ) {
      colorDifferentThanCalendar = true
      eventGroupColor = parentCalendarColor
    }
  }

  const hasLeftIndicator =
    taskDesign || eventGroupColor !== undefined || colorDifferentThanCalendar

  const isTaskOrNote =
    props.event.Type === DocType.TASK || props.event.Type === DocType.NOTE
  const lengthInDays = eventLengthInDays(props.event)

  let smallEvent =
    !props.alwaysFullSize &&
    (eventDuration < smallEventGap ||
      props.event.AllDay === '1' ||
      props.event.late ||
      isMonthView ||
      props.event._id?.length === 0)

  let isInAlldaySection =
    (props.event.AllDay === '1' || props.event.late) && !props.alwaysFullSize

  if (isListView) {
    smallEvent = false
    isInAlldaySection = false
    direction = 'column'
    fontSize = '16px'
  }

  const pictureAndStatus = (
    <Grid
      item
      display={{ xs: 'none', lg: 'flex' }}
      flexDirection={direction === 'column' ? 'column' : 'row'}
      alignItems={'center'}
      height={'100%'}
    >
      <div>
        {!props.searchMode &&
        (eventView === CalendarRange.MONTH || props.embedMode || isGuest) ? (
          <></>
        ) : allCalendarMode || isConvertedGroup(props.event?.referenceGroup) ? (
          <Avatar
            name=""
            size="xxs"
            asGroup
            img={props.event?.referenceGroup?.Photo}
            emptyPic={defaultGroupImage(props.event?.referenceGroup)}
          />
        ) : (
          <Avatar
            asGroup={isGuest}
            emptyPic={defaultGroupImage(props.event?.referenceGroup)}
            img={
              isCurrentUserOwner && !isTrulyEmpty(props.profile?.ProfilePicture)
                ? props.profile?.ProfilePicture
                : isTrulyEmpty(ownerParticipant?.PhotoURL)
                  ? defaultGroupImage(props.event?.referenceGroup)
                  : ownerParticipant?.PhotoURL
            }
            size="xxs"
            name={''}
          />
        )}
      </div>
      {isConvertedGroupId(props.event.GroupID ?? '') ||
      props.embedMode ||
      isGuest ? (
        <></>
      ) : (
        <img
          style={{
            overflow: 'hidden'
          }}
          width={statusSize}
          height={statusSize}
          src={eventStatusIcon(props.event)}
        />
      )}
    </Grid>
  )

  const itemLeadingIcons = leadingIcons(props.event)

  const beforeNow = dayjs(props.instanceEnd).isBefore(dayjs().startOf('day'))
  const open = props.closeAll === true ? false : (initialOpenState ?? false)

  const eventTitle =
    props.title === bookedEvent
      ? t('bookedSlot')
      : ((isTrulyEmpty(props.title) ? undefined : props.title) ??
        provideEventTitle(props.event))

  const eventDisplayTitle =
    props.event?._id?.length === 0 && props.event?.local_id?.length === 0
      ? props.event.Type === DocType.TASK
        ? t('newTask')
        : t('newEvent')
      : eventTitle

  function makeEventTitle(tooltip: boolean) {
    if (eventView === CalendarRange.MONTH && !props.searchMode) {
      const isRTL = isTextRTL(eventTitle) // Determine if the text is RTL

      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems={tooltip ? 'start' : 'center'}
          width="100%"
          overflow="hidden"
          gap={1}
        >
          {/* Growing div: Handles text overflow */}
          <div
            style={{
              flexGrow: 1, // Allow the div to grow
              overflow: 'hidden', // Hide overflowing content
              textOverflow: 'clip',
              whiteSpace: tooltip ? undefined : 'nowrap', // Prevent text wrapping
              direction: isRTL ? 'rtl' : 'ltr', // Set text direction dynamically
              textAlign: 'left' // Align text properly for RTL or LTR
            }}
          >
            {eventDisplayTitle}
          </div>
        </Box>
      )
    }

    return eventDisplayTitle
  }

  const eventDetailsLine = [buildEventTime(), props.event.Location?.Address]
    .filter((value) => !isTrulyEmpty(value))
    .join(', ')

  // Compute how many lines of the title can fit cleanly inside the event
  // rectangle (no half-line cut). FullCalendar slot is 3em (~48px) per hour,
  // so a minute is ~0.8px. Title line-height is 16px.
  const SLOT_PX_PER_MIN = 0.8
  const TITLE_LINE_HEIGHT = 16
  const VERTICAL_PADDING = 6 // top padding + a bit of safety
  const availablePx = Math.max(
    TITLE_LINE_HEIGHT,
    eventDuration * SLOT_PX_PER_MIN - VERTICAL_PADDING
  )
  const totalAvailableLines = Math.max(
    1,
    Math.floor(availablePx / TITLE_LINE_HEIGHT)
  )

  // Whether the event lives in a context where a separate time/location
  // line is meaningful (i.e. not month / list / all-day).
  const detailsLineContext =
    eventView !== CalendarRange.MONTH &&
    eventView !== CalendarRange.LISTWEEK &&
    props.event.AllDay !== '1' &&
    !props.event.late

  // Decide compact vs full layout from the actual measured line budget,
  // not from a duration heuristic. If we have room for 2+ lines and we're
  // in a context that has a time line, show the time/location row — even
  // if the title is long. This mirrors Google Calendar behaviour: time is
  // always shown whenever there is room for a second line.
  const shouldShowTimeLine =
    !props.searchMode &&
    detailsLineContext &&
    totalAvailableLines >= 2 &&
    !isTrulyEmpty(eventDetailsLine)
  const showCompactTitleOnly =
    !props.searchMode &&
    (smallEvent || detailsLineContext) &&
    !shouldShowTimeLine

  // Allocate lines between title and details. Both share the same line
  // budget, capped so each leaves at least one line for the other. Combined
  // with flex layout (title flex:1, details flex:0 0 auto), the details row
  // takes only as many lines as its content actually needs, and the title
  // takes the remainder. This way:
  //   - tight events (2 total lines): title 1, details 1
  //   - tall events: a long location can wrap to many lines
  //   - short details + long title: title fills almost the whole rectangle
  const titleMaxLines = showCompactTitleOnly
    ? 1
    : shouldShowTimeLine
      ? Math.max(1, totalAvailableLines - 1)
      : Math.max(1, totalAvailableLines)
  const detailsMaxLines = shouldShowTimeLine
    ? Math.max(1, totalAvailableLines - 1)
    : 0

  const useFloatingPicture =
    isGroupcalApp() &&
    eventView !== CalendarRange.MONTH &&
    !isInAlldaySection &&
    !isListView &&
    !props.searchMode &&
    !props.embedMode &&
    !isGuest &&
    !showCompactTitleOnly &&
    titleMaxLines >= 2

  const floatingPictureAndStatus = useFloatingPicture ? (
    <div
      style={{
        float: dir === 'rtl' ? 'left' : 'right',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: 32,
        justifyContent: 'space-between',
        marginLeft: dir === 'rtl' ? 0 : 4,
        marginRight: dir === 'rtl' ? 4 : 0,
        pointerEvents: 'none'
      }}
    >
      {pictureAndStatus}
    </div>
  ) : null
  return (
    <div
      id="event-root"
      style={{
        height: isListView ? 'auto' : '100%',
        width: '100%',
        zIndex: 1001
      }}
    >
      {taskDesign && isListView ? (
        <Item
          item={props.event}
          title={props.title}
          hideTime
          setShowEvent={() => {}}
          withToggle
          account={props.account}
          userSettings={props.userSettings}
          event={props.event}
          open={false}
          setOpen={() => {}}
          instanceStart={props.instanceStart}
          instanceEnd={props.instanceEnd}
          onClose={props.onEventDetailsClose}
        />
      ) : (
        <Styled.EventContent
          id={eventIdForContainer(props.event, props.instanceStart)}
          data-color={color}
          data-rev={props.event._rev}
          sx={{
            position: isPendingEvent ? 'relative' : undefined,
            zIndex: isPendingEvent ? 12 : undefined,
            boxShadow: isPendingEvent
              ? `${pendingPopoverShadow} !important`
              : undefined,
            transition: isPendingEvent ? 'box-shadow 0.3s ease' : undefined,
            fontWeight:
              eventView === CalendarRange.WEEK ||
              eventView === CalendarRange.DAY
                ? 500
                : 400,
            border:
              !isMonthView || taskDesign
                ? `1px solid ${taskDesign ? (theme.palette.mode === 'dark' ? 'transparent' : theme.palette.text.primary) : 'white'}`
                : undefined,
            height:
              eventView === CalendarRange.LISTWEEK ||
              eventView === CalendarRange.LISTDAY
                ? '47px'
                : props.alwaysFullSize &&
                    (props.event.AllDay === '1' || props.event.late) &&
                    (props.event.Type === DocType.TASK ||
                      props.event.Type === DocType.NOTE)
                  ? '19px !important'
                  : '100%',
            minHeight:
              eventView === CalendarRange.LISTWEEK ||
              eventView === CalendarRange.LISTDAY ||
              props.alwaysFullSize
                ? '47px'
                : isMonthView
                  ? undefined
                  : '19px',
            backgroundColor:
              taskColor !== defaultTaskColor
                ? theme.palette.mode === 'dark'
                  ? darken(taskColor, 0.55)
                  : lighten(taskColor, 0.95)
                : isTaskOrNote
                  ? 'var(--color-surface-hover)'
                  : 'undefined',
            borderRadius: (() => {
              if (!props.dynamicDesign) return '5px'

              if (!props.isStart && !props.isEnd && lengthInDays > 1) {
                return '0px' // No border-radius for the middle of the event
              } else if (props.isStart && lengthInDays > 1) {
                return '5px 0 0 5px' // Very round border on the left side
              } else if (props.isEnd && lengthInDays > 1) {
                return '0 5px 5px 0' // Very round border on the right side
              } else {
                return '5px' // Default border-radius
              }
            })()
          }}
          color={color}
          fontSize={fontSize}
          style={{ direction: dir }}
          fontcolor={
            taskDesign
              ? theme.palette.text.primary
              : isColorLight(color ?? APP_COLOR, 0.8)
                ? theme.palette.common.black
                : theme.palette.common.white
          }
          className={
            props.ignoreDim || is24meApp()
              ? undefined
              : fullyVisibleEvent
                ? beforeNow
                  ? 'before-now'
                  : 'regular'
                : 'animate-opacity'
          }
          showstripes={pending && !props.embedMode ? 'true' : undefined}
          ref={eventRef}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          onMouseUp={clearDraggingFlag}
          onTouchEnd={clearDraggingFlag}
          onClick={(e) => {
            // e.preventDefault()
            // e.stopPropagation()

            const requestedVisibleEvent = LocalStorage.get(
              LocalStorageKey.VISIBLE_EVENT
            )

            if (
              !isTrulyEmpty(requestedVisibleEvent) &&
              requestedVisibleEvent !== currentEventContainerId
            ) {
              return
            }

            if (props.event?.someday === '1') {
              return
            }

            if (fullyVisibleEvent || props.ignoreDim) {
              openPopover(
                currentEventContainerId || props.event?.id || '',
                <EventPopupContent
                  open={false}
                  awayClickTriggered={false}
                  setAwayClickTriggered={function (b: boolean): void {}}
                  event={props.event!}
                  group={props.group}
                  showSmallUI={isGuest || (props.embedMode ?? false)}
                  userSettings={props.userSettings!}
                  account={props.account!}
                  groups={props.groups}
                  onClose={() => {
                    closePopover()
                  }}
                  instanceStart={props.instanceStart}
                  instanceEnd={props.instanceEnd}
                  fc_id={props.fc_id ?? ''}
                />,
                false,
                undefined,
                e.currentTarget
              )
            } else {
              const divsToAnimate =
                document.querySelectorAll('.animate-opacity')
              divsToAnimate.forEach(
                (div) => ((div as any).style.opacity = beforeNow ? 0.6 : 1)
              )
              const animationTimeout = setTimeout(() => {
                divsToAnimate.forEach(
                  (div) => ((div as any).style.opacity = '0.1')
                )
              }, 2000)

              setTimeout(() => {
                if (tour.isOpen) {
                  // do not open dimmed in case of other is active
                  return
                }

                tfmTourProvider.setMode(ONBOARDING_MODE.DIMMED)
                if (tour.setSteps && tfmTourProvider.getStepsByMode) {
                  tour.setSteps(
                    tfmTourProvider.getStepsByMode(ONBOARDING_MODE.DIMMED)
                  )
                }

                tour.setCurrentStep(0)
                tour.setIsOpen(true)
              }, 2000)
            }
          }}
        >
          {hasLeftIndicator && !props.embedMode && (
            <Grid
              item
              id={`${eventIdForContainer(props.event, props.instanceStart)}-indicator`}
              data-indicator-color={eventGroupColor}
              xs={'auto'}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              style={{
                width: '4px',
                borderTopLeftRadius: dir === 'rtl' ? 0 : 8,
                borderBottomLeftRadius: dir === 'rtl' ? 0 : 8,
                borderTopRightRadius: dir === 'rtl' ? 8 : 0,
                borderBottomRightRadius: dir === 'rtl' ? 8 : 0,
                overflow: 'hidden',
                backgroundColor:
                  isGroupcalApp() ||
                  props.event?.TaskType === TaskType.personalEvent
                    ? eventGroupColor
                      ? darken(eventGroupColor, 0.15)
                      : undefined
                    : taskColor
              }}
            />
          )}
          <Grid
            xs
            item
            height={'100%'}
            overflow={'hidden'}
            paddingLeft={
              eventView === CalendarRange.MONTH ? '0.15rem' : '0.3rem'
            }
            paddingRight={
              eventView === CalendarRange.MONTH ? '0.15rem' : '0.3rem'
            }
            paddingTop={
              eventView === CalendarRange.MONTH || isInAlldaySection
                ? 0
                : '0.15rem'
            }
          >
            <Grid
              overflow={isListView ? 'visible' : 'hidden'}
              display={'flex'}
              width={'100%'}
              height={isListView ? 'auto' : '100%'}
              flexDirection={'column'}
            >
              {props.event.someday === '1' ? (
                <Grid
                  container
                  display={'flex'}
                  wrap="nowrap"
                  alignItems={
                    isInAlldaySection || isMonthView ? 'center' : undefined
                  }
                  flexDirection={smallEvent ? 'row' : 'column'}
                >
                  <SomedayItem title={props.event.Text} />
                </Grid>
              ) : (
                <GroupcalTooltip
                  placement="top"
                  hidden={props.event.Text?.length === 0}
                  enterDelay={200}
                  title={
                    <Box
                      display={'flex'}
                      flexDirection={'column'}
                      alignItems={dir === 'rtl' ? 'end' : 'start'}
                      lineHeight={'18px'}
                    >
                      <Box
                        gap={1}
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'start'}
                        sx={{
                          '@media print': {
                            color: 'black !important'
                          }
                        }}
                      >
                        {itemLeadingIcons}
                        {makeEventTitle(true) ?? ''}
                      </Box>
                      <div style={{ alignItems: 'start' }}>{eventTimeText}</div>
                      <div>{props.event?.Location?.Address}</div>
                    </Box>
                  }
                >
                  {eventView === CalendarRange.MONTH && !props.searchMode ? (
                    <Grid
                      container
                      display={'flex'}
                      wrap="nowrap"
                      alignItems={'center'}
                      flexDirection={'row'}
                      style={{ flex: 1, minHeight: 0 }}
                    >
                      <Grid
                        item
                        xs
                        gap={1}
                        flexDirection={'row'}
                        display={'flex'}
                        color={
                          props.event.Priority == '2'
                            ? 'var(--color-negative)'
                            : undefined
                        }
                        style={{
                          lineHeight: '16px',
                          textDecoration: isInstanceCompleted(
                            props.event,
                            props.instanceStart
                          )
                            ? 'line-through'
                            : 'none',
                          wordBreak: 'break-word',
                          overflow: 'hidden'
                        }}
                      >
                        {makeEventTitle(false)}
                      </Grid>
                    </Grid>
                  ) : (
                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: isListView
                          ? undefined
                          : `${totalAvailableLines * 16}px`,
                        // When the picture is floated we need a regular block
                        // container (not flex) so that the float can overhang
                        // from the title into the subtitle, and the subtitle
                        // sits flush directly below the title text.
                        display: useFloatingPicture ? 'block' : 'flex',
                        flexDirection: 'column',
                        overflow: isListView ? 'visible' : 'hidden',
                        color:
                          props.event.Priority == '2'
                            ? 'var(--color-negative)'
                            : undefined
                      }}
                    >
                      {floatingPictureAndStatus}
                      <div
                        style={{
                          flex: '0 0 auto',
                          maxHeight: isListView
                            ? undefined
                            : showCompactTitleOnly
                              ? undefined
                              : `${titleMaxLines * 16}px`,
                          minHeight: 0,
                          // With a floated sibling above we want the float to
                          // visually overhang into this block. `overflow:
                          // hidden` would establish a BFC and prevent that,
                          // but `overflow: visible` lets long text paint over
                          // the subtitle. `clip-path` clips paint *without*
                          // creating a BFC, so the float still overhangs and
                          // overflowing text is hidden cleanly.
                          overflow: useFloatingPicture ? 'visible' : 'hidden',
                          clipPath: useFloatingPicture ? 'inset(0)' : undefined,
                          wordBreak: 'break-word',
                          whiteSpace: showCompactTitleOnly
                            ? 'nowrap'
                            : 'normal',
                          textOverflow: 'clip',
                          lineHeight:
                            eventView === CalendarRange.LISTWEEK ||
                            eventView === CalendarRange.LISTDAY
                              ? undefined
                              : '16px',
                          textDecoration: isInstanceCompleted(
                            props.event,
                            props.instanceStart
                          )
                            ? 'line-through'
                            : 'none',
                          direction: dir as 'ltr' | 'rtl'
                        }}
                      >
                        {eventDisplayTitle}
                        {eventView === CalendarRange.LISTWEEK && location && (
                          <LocationLabel>
                            {props.event.Location?.Address}
                          </LocationLabel>
                        )}
                      </div>
                      {shouldShowTimeLine &&
                        !isTrulyEmpty(eventDetailsLine) && (
                          <div
                            style={{
                              flex: '0 0 auto',
                              minHeight: '16px',
                              color:
                                props.event.TaskType ===
                                  TaskType.personalEvent ||
                                props.event.Type === DocType.EVENT
                                  ? isColorLight(color ?? APP_COLOR, 0.8)
                                    ? theme.palette.text.primary
                                    : theme.palette.common.white
                                  : theme.palette.text.primary,
                              fontSize: '12px',
                              lineHeight: '16px',
                              fontWeight: 300,
                              maxHeight: `${detailsMaxLines * 16}px`,
                              overflow: useFloatingPicture
                                ? 'visible'
                                : 'hidden',
                              clipPath: useFloatingPicture
                                ? 'inset(0)'
                                : undefined,
                              whiteSpace: 'normal',
                              textOverflow: 'clip',
                              wordBreak: 'break-word'
                            }}
                          >
                            {eventDetailsLine}
                          </div>
                        )}
                    </div>
                  )}
                </GroupcalTooltip>
              )}
            </Grid>
          </Grid>
          {!useFloatingPicture && (
            <Grid
              item
              xs={'auto'}
              paddingRight={'0.3rem'}
              paddingLeft={'0.15rem'}
              paddingTop={isInAlldaySection ? 0 : '0.15rem'}
            >
              {isGroupcalApp() ? pictureAndStatus : <></>}
            </Grid>
          )}
        </Styled.EventContent>
      )}
    </div>
  )
}

interface SubTextProps {
  text1?: string | undefined | null
  text2?: string | undefined | null
}

export function SubText(props: SubTextProps) {
  return (
    <Box
      sx={{
        fontSize: '12px',
        fontWeight: 300,
        overflow: 'hidden',
        lineHeight: '16px',
        whiteSpace: 'pre-line',
        textOverflow: 'clip',
        'media print': {
          color: 'black !important'
        }
      }}
    >
      {props.text1}
      {props.text2}
    </Box>
  )
}

function arePropsEqual(prev: EventProps, next: EventProps): boolean {
  // Watermelon may mutate the same Model instance, so compare by _rev rather
  // than reference. _rev is bumped on every record update.
  if (prev.event?._rev !== next.event?._rev) return false
  if (prev.event?._id !== next.event?._id) return false
  if (prev.event?.GroupID !== next.event?.GroupID) return false
  if (prev.event?.ThirdPartyID !== next.event?.ThirdPartyID) return false
  if (prev.event?.TaskType !== next.event?.TaskType) return false
  if (prev.event?.Type !== next.event?.Type) return false
  if (prev.event?.referenceGroup?._id !== next.event?.referenceGroup?._id)
    return false
  if (prev.account?._rev !== next.account?._rev) return false
  if (prev.userSettings?._rev !== next.userSettings?._rev) return false
  if (prev.profile?._rev !== next.profile?._rev) return false
  if (prev.masterLabel?._rev !== next.masterLabel?._rev) return false
  if (prev.group?._rev !== next.group?._rev) return false
  if (prev.topGroup?._rev !== next.topGroup?._rev) return false
  if (prev.event?.Location?.Address !== next.event?.Location?.Address)
    return false
  if (prev.event?.OwnerID !== next.event?.OwnerID) return false
  if (prev.event?.PersonalOwnerId !== next.event?.PersonalOwnerId) return false

  const prevNotes = prev.event?.Notes
  const nextNotes = next.event?.Notes

  if (!prevNotes && nextNotes) return false
  if (prevNotes && !nextNotes) return false

  if (prevNotes && nextNotes) {
    if (typeof prevNotes === 'string' && typeof nextNotes === 'string') {
      if (prevNotes !== nextNotes) return false
    } else {
      if (prevNotes.length !== nextNotes.length) return false

      for (let i = 0; i < Math.max(prevNotes.length, nextNotes.length); i++) {
        if (prevNotes[i].Note !== nextNotes[i].Note) return false
      }
    }
  }

  const prevParticipants = prev.localParticipants
  const nextParticipants = next.localParticipants
  if (prevParticipants !== nextParticipants) {
    if (prevParticipants?.length !== nextParticipants?.length) return false
    if (prevParticipants && nextParticipants) {
      for (let i = 0; i < prevParticipants.length; i++) {
        if (prevParticipants[i]?._id !== nextParticipants[i]?._id) return false
      }
    }
  }
  const prevReminders = prev.event?.Reminder
  const nextReminders = next.event?.Reminder

  if (!prevReminders && nextReminders) return false
  if (prevReminders && !nextReminders) return false

  if (prevReminders && nextReminders) {
    if (prevReminders.length !== nextReminders.length) return false

    for (
      let i = 0;
      i < Math.max(prevReminders.length, nextReminders.length);
      i++
    ) {
      if (prevReminders[i].AlertTime !== nextReminders[i].AlertTime)
        return false
      if (prevReminders[i].AlertTime !== nextReminders[i].AlertTime)
        return false
    }
  }

  return (
    prev.title === next.title &&
    prev.instanceStart === next.instanceStart &&
    prev.instanceEnd === next.instanceEnd &&
    prev.isStart === next.isStart &&
    prev.isEnd === next.isEnd &&
    prev.closeAll === next.closeAll &&
    prev.alwaysFullSize === next.alwaysFullSize &&
    prev.onEventDetailsClose === next.onEventDetailsClose &&
    prev.ignoreDim === next.ignoreDim &&
    prev.calendarView === next.calendarView &&
    prev.doNotScrollToEvent === next.doNotScrollToEvent &&
    prev.fc_id === next.fc_id &&
    prev.event?.Location?.Address === next.event?.Location?.Address &&
    prev.event?.Color === next.event?.Color &&
    prev.dynamicDesign === next.dynamicDesign &&
    prev.embedMode === next.embedMode &&
    prev.searchMode === next.searchMode
  )
}

const MemoizedEvent = memo(Event, arePropsEqual)

// Use this when rendering Event inside <EventGlobalsProvider> (CalendarView, ListView,
// EventContent). It reads account/userSettings/profile/participants/masterLabel/topGroup
// and the per-event group from context, avoiding 7 watermelon subscriptions per event card.
export const EventBare = MemoizedEvent

export default withObservables(['event'], (props: EventProps) => ({
  account: ObserveService.Account(),
  group: ObserveService.GroupByServerIDRestricted(
    props.event?.GroupID ?? LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
  ),
  userSettings: ObserveService.UserSettings(),
  topGroup: ObserveService.FirstGroup(props.userSettings),
  profile: ObserveService.Profile(),
  localParticipants: ObserveService.Participants(),
  masterLabel: ObserveService.MasterLabel()
}))(Event as any)

export function leadingIcons(event: EventDoc) {
  if (!event.Type) return <></>

  const size = 10
  const color = 'white'
  let icon
  switch (event.Type as DocType) {
    case DocType.EVENT:
      icon = undefined
      break
    case DocType.TASK:
      icon = <TypeTask color="white" width={size} height={size} />
      break
    case DocType.NOTE:
      icon = <TypeNote color="white" width={size} height={size} />
      break
  }

  const divStyle: CSSProperties = {}

  const hasIcons =
    icon ||
    (event.Recurrence &&
      typeof event.Recurrence !== 'string' &&
      Object.keys(event.Recurrence).length != 0) ||
    (event.Type != DocType.NOTE &&
      event.Notes &&
      typeof event.Notes !== 'string' &&
      event.Notes.length > 0)

  if (!hasIcons) return <></>

  return (
    <Grid item display={'flex'} flexDirection={'row'} gap={'0.25rem'}>
      {icon && <div style={divStyle}>{icon}</div>}
      {event.Recurrence &&
        typeof event.Recurrence !== 'string' &&
        Object.keys(event.Recurrence).length != 0 && (
          <div style={divStyle}>
            <RecurrentItemCalendar color={color} width={size} height={size} />
          </div>
        )}
      {event.Type != DocType.NOTE &&
        event.Notes &&
        typeof event.Notes !== 'string' &&
        event.Notes.length > 0 && (
          <div style={divStyle}>
            <Notes width={size} height={size} />
          </div>
        )}
    </Grid>
  )
}

function isTaskDesign(event?: EventDoc): boolean {
  return event?.Type === DocType.TASK || event?.Type === DocType.NOTE
}

export function isInstanceCompleted(
  event: EventDoc,
  instanceStart: number
): boolean {
  if (isGroupcalApp()) return false

  const statusAsString = String(event.Status)

  if (!event.Recurrence) return statusAsString === EventStatus.COMPLETED

  if (statusAsString !== EventStatus.ACTIVE) return true

  if (
    event.Recurrence &&
    event.Recurrence.Exclusion &&
    typeof event.Recurrence.Exclusion !== 'string'
  ) {
    const instance = event.Recurrence.Exclusion?.find(
      (exclusion) =>
        dayjs.unix(Number(exclusion.Date)).format('YYYY-MM-DD') ===
        dayjs(instanceStart).format('YYYY-MM-DD')
    )

    return (
      String(findStatusForInstance(event, instanceStart)) ===
      EventStatus.COMPLETED
    )
  }

  return false
}

export function instanceStatus(
  event: EventDoc,
  instanceStart: number
): EventStatus {
  if (!event.Recurrence) return event.Status ?? EventStatus.ACTIVE

  if (event.Status !== EventStatus.ACTIVE)
    return event.Status ?? EventStatus.ACTIVE

  if (
    event.Recurrence &&
    event.Recurrence.Exclusion &&
    typeof event.Recurrence.Exclusion !== 'string'
  ) {
    const instance = event.Recurrence.Exclusion?.find(
      (exclusion) =>
        dayjs.unix(Number(exclusion.Date)).format('YYYY-MM-DD') ===
        dayjs(instanceStart).format('YYYY-MM-DD')
    )
    return String(findStatusForInstance(event, instanceStart)) as EventStatus
  }

  return event.Status ?? EventStatus.ACTIVE
}
